import { useState } from "react";

import { useTranslation } from "react-i18next";

import { userFormattedName } from "@/models/UserModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Button } from "@/uiKit/atoms/Button";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { ColoredCard } from "@/uiKit/atoms/ColoredCard.tsx";
import { ProfilePicture } from "@/uiKit/atoms/ProfilePicture.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { PatientRenewal } from "@/uiKit/organisms/PatientCreationAndRenewal/PatientRenewal.tsx";
import { toDateTime } from "@/utils/dateUtils.ts";
import { formatNumber } from "@/utils/mathUtils.ts";

export const NurseReinclusionRequestBanner = ({ patientId }: { patientId: string }) => {
  const { t } = useTranslation();
  const refuseReinclusion = Queries.practitioner.useDoctorRefuseReinclusion();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const reinclusion = Queries.practitioner.useReinclusionStateForDoctor(patientId);

  if (!reinclusion?.reinclusionRequestActive) {
    return null;
  }
  if (!reinclusion.renewalType) {
    return null;
  }
  const daysSinceLastTLS = reinclusion.lastCarePlanEnd
    ? formatNumber(-toDateTime(reinclusion.lastCarePlanEnd).diffNow("days").days, 0)
    : undefined;

  return (
    <ColoredCard id="reinclusion-request-banner" square border color="blue">
      <CardHeader
        startIcon={
          reinclusion.nurseFirstName &&
          reinclusion.nurseLastName && (
            <ProfilePicture
              id="reinclusion-request-nurse-avatar"
              src={reinclusion.nurseProfilePicture?.url}
              firstName={reinclusion.nurseFirstName}
              lastName={reinclusion.nurseLastName}
              size="medium"
              colorVariant="secondary"
              tooltip={userFormattedName(reinclusion.nurseLastName, reinclusion.nurseFirstName)}
            />
          )
        }
        variant="banner"
        title={
          reinclusion.nurseFirstName && reinclusion.nurseLastName
            ? t("reinclusion.doctorTitleWithNurseName", {
                nurseName: userFormattedName(reinclusion.nurseLastName, reinclusion.nurseFirstName),
                days: daysSinceLastTLS,
              })
            : t("reinclusion.doctorTitle", {
                days: daysSinceLastTLS,
              })
        }
      />
      {reinclusion.nurseMessage && (
        <CardContent>
          {reinclusion.nurseMessage && <Typography variant="body">{reinclusion.nurseMessage}</Typography>}
        </CardContent>
      )}
      <CardFooter position="start">
        <Button
          variant="contained"
          data-action="inclusion.reinclusion.accept"
          onClick={() => setIsModalOpen(true)}
          id="accept-reinclusion-request"
        >
          {t("reinclusion.doctorCTAAccept")}
        </Button>
        <Button
          id="deny-reinclusion-request"
          pending={refuseReinclusion.isPending}
          variant="contained"
          data-action="inclusion.reinclusion.reject"
          onClick={() => refuseReinclusion.mutate(patientId)}
        >
          {t("reinclusion.doctorCTADeny")}
        </Button>
      </CardFooter>
      <PatientRenewal
        renewalType={reinclusion.renewalType}
        patientId={patientId}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={() => setIsModalOpen(false)}
      />
    </ColoredCard>
  );
};
